import { memo, useCallback, useEffect, useState } from 'react'

import { Box, MenuItem, Select, SelectProps, TextField, TextFieldProps } from '@mui/material'

import { formatDuration } from 'utils'

const conversionToSeconds = {
  sec: 1,
  min: 60,
  h: 60 * 60,
  d: 60 * 60 * 24,
  y: 60 * 60 * 24 * 365,
}

type DurationFieldProps = {
  defaultValue: number
  onChange: (value: number) => void
  hasError?: boolean
  disabled?: boolean
  label?: string
  size?: TextFieldProps['size']
  textFieldProps?: TextFieldProps
  selectProps?: SelectProps
  noYears?: boolean
  noDays?: boolean
  noHours?: boolean
  noMinutes?: boolean
  noSeconds?: boolean
}

const DurationFieldRaw = ({
  defaultValue,
  onChange,
  hasError,
  disabled,
  label,
  size,
  noYears,
  noDays,
  noHours,
  noMinutes,
  noSeconds,
  textFieldProps,
  selectProps,
}: DurationFieldProps) => {
  const [value, setValue] = useState('0')
  const [measure, setMeasure] = useState<keyof typeof conversionToSeconds>('d')
  const [computedValue, setComputedValue] = useState(0)

  const handleValueChange = useCallback(
    (value: string) => {
      const computedValue = parseFloat(value) * conversionToSeconds[measure]

      setValue(value)
      setComputedValue(computedValue)
      onChange(computedValue)
    },
    [measure, onChange],
  )

  const handleMeasureChange = useCallback(
    (measure: keyof typeof conversionToSeconds) => {
      const computedValue = parseFloat(value) * conversionToSeconds[measure]

      setMeasure(measure)
      setComputedValue(computedValue)
      onChange(computedValue)
    },
    [onChange, value],
  )

  useEffect(() => {
    if (defaultValue === computedValue) return

    const formattedDurationArray = formatDuration(
      defaultValue,
      { seconds: true, minutes: true, hours: true, days: true, years: true },
      { suffixed: true, omitEmptyValues: true },
    )

    if (formattedDurationArray.length) {
      setValue((formattedDurationArray[0] as string).split(' ')[0])
      setMeasure((formattedDurationArray[0] as string).split(' ')[1] as keyof typeof conversionToSeconds)
    }
  }, [defaultValue, computedValue])

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <TextField
        sx={{ width: 150, alignSelf: 'flex-start' }}
        value={value}
        autoFocus
        label={label}
        disabled={disabled}
        error={hasError}
        type="number"
        fullWidth
        required
        autoComplete="off"
        onChange={(e) => handleValueChange(e.target.value)}
        {...textFieldProps}
        size={textFieldProps?.size || size}
      />
      <Select
        sx={{ width: 150, alignSelf: 'flex-start' }}
        value={measure}
        disabled={disabled}
        onChange={(e) => handleMeasureChange(e.target.value as keyof typeof conversionToSeconds)}
        fullWidth
        {...selectProps}
        size={selectProps?.size || size}
      >
        {!noSeconds && <MenuItem value="sec">Seconds</MenuItem>}
        {!noMinutes && <MenuItem value="min">Minutes</MenuItem>}
        {!noHours && <MenuItem value="h">Hours</MenuItem>}
        {!noDays && <MenuItem value="d">Days</MenuItem>}
        {!noYears && <MenuItem value="y">Years</MenuItem>}
      </Select>
    </Box>
  )
}

export const DurationField = memo(DurationFieldRaw)
